/**
 * Google Ads connector.
 *
 * The Google Ads API has no CORS support and requires a developer-token
 * header that's an Auroinfo-side credential. So this connector talks to
 * our @agencylens/worker-google-ads-proxy Worker instead of directly to
 * googleads.googleapis.com.
 *
 * Required OAuth scope: https://www.googleapis.com/auth/adwords
 */

import type {
  GoogleAdsAccount,
  GoogleAdsReportData,
  GoogleAdsDailyRow,
  GoogleAdsCampaignRow,
} from "@agencylens/shared";

export class GoogleAdsError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly hint?: string,
  ) {
    super(message);
    this.name = "GoogleAdsError";
  }
}

export class GoogleAdsConfigError extends GoogleAdsError {
  constructor(hint: string) {
    super("Google Ads is not configured for AgencyLens yet.", 503, hint);
    this.name = "GoogleAdsConfigError";
  }
}

/* --------------------------------------------------- low-level fetch */

interface AdsFetchOptions {
  accessToken: string;
  proxyBase: string;
}

async function proxyGet(
  opts: AdsFetchOptions,
  path: string,
): Promise<unknown> {
  const res = await fetch(`${opts.proxyBase}${path}`, {
    headers: { Authorization: `Bearer ${opts.accessToken}` },
  });
  return readProxyResponse(res, `GET ${path}`);
}

async function proxyPost(
  opts: AdsFetchOptions,
  path: string,
  body: unknown,
): Promise<unknown> {
  const res = await fetch(`${opts.proxyBase}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${opts.accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return readProxyResponse(res, `POST ${path}`);
}

async function readProxyResponse(res: Response, label: string): Promise<unknown> {
  const text = await res.text();
  let data: unknown = null;
  try {
    data = JSON.parse(text);
  } catch {
    throw new GoogleAdsError(
      `${label} returned non-JSON ${res.status}: ${text.slice(0, 200)}`,
      res.status,
    );
  }
  if (!res.ok) {
    const obj = (data as { error?: string; hint?: string }) ?? {};
    if (obj.error === "developer_token_not_configured") {
      throw new GoogleAdsConfigError(
        obj.hint ?? "Operator must provision GOOGLE_ADS_DEVELOPER_TOKEN.",
      );
    }
    throw new GoogleAdsError(
      `${label} failed: ${res.status} ${JSON.stringify(data).slice(0, 200)}`,
      res.status,
    );
  }
  return data;
}

/* --------------------------------------------------- listAccessibleCustomers */

interface ListAccessibleCustomersResponse {
  resourceNames?: string[];
}

export async function listAccessibleCustomerIds(
  opts: AdsFetchOptions,
): Promise<string[]> {
  const data = (await proxyGet(opts, "/list-accessible-customers")) as
    | ListAccessibleCustomersResponse
    | { error: string };
  if ("error" in data) {
    throw new GoogleAdsError(`listAccessibleCustomers: ${data.error}`);
  }
  return (data.resourceNames ?? []).map((rn) => rn.split("/")[1] ?? "").filter(Boolean);
}

/* --------------------------------------------------- search helper */

interface SearchResponseRow {
  customer?: {
    id?: string;
    descriptiveName?: string;
    currencyCode?: string;
    timeZone?: string;
    manager?: boolean;
  };
  campaign?: {
    id?: string;
    name?: string;
    status?: string;
  };
  metrics?: {
    impressions?: string;
    clicks?: string;
    costMicros?: string;
    conversions?: number | string;
    conversionsValue?: number | string;
  };
  segments?: {
    date?: string;
  };
}

interface SearchResponse {
  results?: SearchResponseRow[];
  nextPageToken?: string;
}

export async function search(
  opts: AdsFetchOptions,
  customerId: string,
  query: string,
  loginCustomerId?: string,
): Promise<SearchResponseRow[]> {
  const body: Record<string, string> = { customerId, query };
  if (loginCustomerId) body["loginCustomerId"] = loginCustomerId;
  const data = (await proxyPost(opts, "/search", body)) as SearchResponse;
  return data.results ?? [];
}

/* --------------------------------------------------- listAccounts */

/**
 * Fetch friendly account info for each accessible customer.
 *
 * The user's accessible customers might include MCC managers. We surface them
 * all and let the agency owner pick the one they want to report on. Walking
 * MCC → child accounts is a follow-up.
 */
export async function listAccounts(
  opts: AdsFetchOptions,
): Promise<GoogleAdsAccount[]> {
  const ids = await listAccessibleCustomerIds(opts);
  const out: GoogleAdsAccount[] = [];
  for (const id of ids) {
    try {
      const rows = await search(
        opts,
        id,
        "SELECT customer.id, customer.descriptive_name, customer.currency_code, customer.time_zone, customer.manager FROM customer",
      );
      const c = rows[0]?.customer;
      if (!c?.id) continue;
      out.push({
        customerId: c.id,
        descriptiveName: c.descriptiveName ?? `Account ${c.id}`,
        currencyCode: c.currencyCode ?? "USD",
        timeZone: c.timeZone ?? "UTC",
        isManager: Boolean(c.manager),
      });
    } catch (e) {
      // One bad account shouldn't break the list. Log and continue.
      console.warn(`Google Ads customer ${id} details fetch failed`, e);
    }
  }
  return out;
}

/* --------------------------------------------------- fetchReport */

function microsToMajor(micros: number | string | undefined): number {
  if (micros == null) return 0;
  const n = typeof micros === "string" ? Number(micros) : micros;
  return Number.isFinite(n) ? n / 1_000_000 : 0;
}

function asNum(v: number | string | undefined): number {
  if (v == null) return 0;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
}

function ga4DateToIso(d: string): string {
  // Google Ads dates are already YYYY-MM-DD
  return d;
}

export async function fetchReport(
  opts: AdsFetchOptions,
  account: GoogleAdsAccount,
  startDate: string,
  endDate: string,
): Promise<GoogleAdsReportData> {
  const dailyQuery = `
    SELECT
      segments.date,
      metrics.impressions,
      metrics.clicks,
      metrics.cost_micros,
      metrics.conversions,
      metrics.conversions_value
    FROM customer
    WHERE segments.date BETWEEN '${startDate}' AND '${endDate}'
    ORDER BY segments.date ASC
  `;
  const campaignQuery = `
    SELECT
      campaign.id,
      campaign.name,
      campaign.status,
      metrics.impressions,
      metrics.clicks,
      metrics.cost_micros,
      metrics.conversions,
      metrics.conversions_value
    FROM campaign
    WHERE segments.date BETWEEN '${startDate}' AND '${endDate}'
    ORDER BY metrics.cost_micros DESC
    LIMIT 25
  `;

  const loginCustomerId = account.loginCustomerId;
  const [dailyRows, campaignRows] = await Promise.all([
    search(opts, account.customerId, dailyQuery, loginCustomerId),
    search(opts, account.customerId, campaignQuery, loginCustomerId),
  ]);

  const daily: GoogleAdsDailyRow[] = dailyRows
    .map((r) => ({
      date: ga4DateToIso(r.segments?.date ?? ""),
      impressions: asNum(r.metrics?.impressions),
      clicks: asNum(r.metrics?.clicks),
      cost: microsToMajor(r.metrics?.costMicros),
      conversions: asNum(r.metrics?.conversions),
      conversionValue: asNum(r.metrics?.conversionsValue),
    }))
    .filter((r) => r.date);

  const campaigns: GoogleAdsCampaignRow[] = campaignRows
    .map((r) => ({
      campaignId: r.campaign?.id ?? "",
      campaignName: r.campaign?.name ?? "(unnamed)",
      status: r.campaign?.status ?? "UNKNOWN",
      impressions: asNum(r.metrics?.impressions),
      clicks: asNum(r.metrics?.clicks),
      cost: microsToMajor(r.metrics?.costMicros),
      conversions: asNum(r.metrics?.conversions),
      conversionValue: asNum(r.metrics?.conversionsValue),
    }))
    .filter((r) => r.campaignId);

  const totalImpressions = daily.reduce((s, r) => s + r.impressions, 0);
  const totalClicks = daily.reduce((s, r) => s + r.clicks, 0);
  const totalCost = daily.reduce((s, r) => s + r.cost, 0);
  const totalConversions = daily.reduce((s, r) => s + r.conversions, 0);
  const totalConversionValue = daily.reduce((s, r) => s + r.conversionValue, 0);

  return {
    account,
    startDate,
    endDate,
    daily,
    campaigns,
    totals: {
      impressions: totalImpressions,
      clicks: totalClicks,
      cost: totalCost,
      conversions: totalConversions,
      conversionValue: totalConversionValue,
      averageCpc: totalClicks > 0 ? totalCost / totalClicks : 0,
      conversionRate: totalClicks > 0 ? totalConversions / totalClicks : 0,
    },
    fetchedAt: new Date().toISOString(),
  };
}

/* --------------------------------------------------- date helpers */

export function isoDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function defaultDateRange(days = 30): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const end = new Date(now);
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return { startDate: isoDate(start), endDate: isoDate(end) };
}

export const GOOGLE_ADS_SCOPE = "https://www.googleapis.com/auth/adwords";
